import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { CheckCircle, Clock, CurrencyEur, Percent, WarningCircle, Bell } from "@phosphor-icons/react";
import { formatEUR, formatDate } from "@/lib/constants";

const MON = ["", "Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];
export const monthLabel = (m) => `${MON[Number(m.slice(5, 7))]} ${m.slice(0, 4)}`;

export const STATUS = {
  open: { label: "Offen", cls: "bg-rose-100 text-rose-800 ring-rose-200" },
  partial: { label: "Teilweise", cls: "bg-amber-100 text-amber-800 ring-amber-200" },
  paid: { label: "Bezahlt", cls: "bg-emerald-100 text-emerald-800 ring-emerald-200" },
  credit: { label: "Guthaben", cls: "bg-sky-100 text-sky-800 ring-sky-200" },
};

export function StatusPill({ status }) {
  const s = STATUS[status];
  return <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ${s.cls}`} data-testid={`os-status-${status}`}>{s.label}</span>;
}

function Kpi({ icon: Icon, label, value, sub, tone, testid }) {
  const tones = { ink: "border-l-slate-900 text-slate-900", green: "border-l-emerald-600 text-emerald-700", red: "border-l-rose-600 text-rose-700", amber: "border-l-amber-500 text-amber-700" };
  return (
    <div className={`rounded-lg border border-stone-200 border-l-4 bg-white p-4 ${tones[tone]}`} data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500"><Icon size={13} /> {label}</div>
      <div className="mt-1.5 text-2xl font-semibold tabular-nums">{value}</div>
      <div className="mt-0.5 text-xs text-slate-500">{sub}</div>
    </div>
  );
}

export function KpiRow({ s }) {
  const q = s.quote_pct == null ? "–" : `${s.quote_pct.toLocaleString("de-DE", { maximumFractionDigits: 1 })} %`;
  return (
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="os-kpis">
      <Kpi icon={CurrencyEur} label="Fällig (Soll)" value={formatEUR(s.soll)} sub={`${s.tenancies} Mietverhältnisse`} tone="ink" testid="os-kpi-soll" />
      <Kpi icon={CheckCircle} label="Eingegangen (Ist)" value={formatEUR(s.ist)} sub={`Differenz ${formatEUR(s.diff)}`} tone={s.diff >= -0.01 ? "green" : "red"} testid="os-kpi-ist" />
      <Kpi icon={Percent} label="Zahlungsquote" value={q} sub={`${s.paid_count} ohne Rückstand`} tone={(s.quote_pct || 0) >= 98 ? "green" : "amber"} testid="os-kpi-quote" />
      <Kpi icon={WarningCircle} label="Rückstände gesamt" value={formatEUR(s.arrears_total)} sub={`bei ${s.arrears_count} Mietverhältnissen`} tone={s.arrears_count ? "red" : "green"} testid="os-kpi-arrears" />
      <Kpi icon={Clock} label="Nicht pünktlich" value={s.late_count} sub={`davon gemahnt: ${s.dunned_count}`} tone={s.late_count ? "amber" : "green"} testid="os-kpi-late" />
    </div>
  );
}

export function MonthChart({ months }) {
  if ((months || []).length < 2) return null;
  const data = months.map((m) => ({ name: monthLabel(m.month), Soll: m.soll, Ist: m.ist }));
  return (
    <div className="rounded-lg border border-stone-200 bg-white p-4" data-testid="os-chart">
      <div className="text-sm font-semibold mb-2">Monatsverlauf</div>
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={data} barGap={2}>
          <CartesianGrid vertical={false} stroke="#e2e8f0" />
          <XAxis dataKey="name" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${Math.round(v / 1000)}k`} width={36} />
          <Tooltip formatter={(v) => formatEUR(v)} />
          <Legend wrapperStyle={{ fontSize: 12 }} formatter={(v) => <span className="text-slate-700">{v}</span>} />
          <Bar dataKey="Soll" fill="#cbd5e1" radius={[3, 3, 0, 0]} />
          <Bar dataKey="Ist" fill="#047857" radius={[3, 3, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

const money = (v, strong) => <span className={`tabular-nums ${strong ? "font-semibold" : ""} ${v < -0.01 ? "text-rose-700" : v > 0.01 ? "text-emerald-700" : "text-slate-700"}`}>{formatEUR(v)}</span>;

export function TenancyTable({ rows }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="os-table">
        <thead className="bg-stone-50 text-[10px] uppercase tracking-wider text-slate-500">
          <tr>{["Mieter", "Objekt / Einheit", "Soll", "Ist", "Differenz", "Kontostand", "Status", "Letzte Zahlung"].map((h, i) => <th key={h} className={`px-3 py-2.5 font-semibold ${i >= 2 && i <= 5 ? "text-right" : i >= 6 ? "text-center" : "text-left"}`}>{h}</th>)}</tr>
        </thead>
        <tbody className="divide-y divide-stone-100">
          {rows.map((r) => (
            <tr key={r.tenancy_id} className="hover:bg-stone-50/70 transition-colors" data-testid={`os-row-${r.tenancy_id}`}>
              <td className="px-3 py-2.5"><a href={`/mietverhaeltnisse/${r.tenancy_id}`} className="font-medium text-slate-900 hover:underline">{r.tenant_name}</a>
                <div className="text-[11px] text-slate-500 font-mono">{r.contract_number}{r.dunnings.length > 0 && <span className="ml-2 inline-flex items-center gap-0.5 font-sans text-rose-700"><Bell size={11} /> {r.dunnings.length}× gemahnt</span>}</div></td>
              <td className="px-3 py-2.5"><div>{r.property_label}</div><div className="text-[11px] text-slate-500">{r.unit_label}</div></td>
              <td className="px-3 py-2.5 text-right tabular-nums">{formatEUR(r.soll)}</td>
              <td className="px-3 py-2.5 text-right tabular-nums">{formatEUR(r.ist)}</td>
              <td className="px-3 py-2.5 text-right">{money(r.diff)}</td>
              <td className="px-3 py-2.5 text-right">{money(r.saldo, true)}</td>
              <td className="px-3 py-2.5 text-center"><StatusPill status={r.status} /></td>
              <td className="px-3 py-2.5 text-center text-slate-600">{r.last_payment ? formatDate(r.last_payment) : "–"}</td>
            </tr>
          ))}
          {!rows.length && <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-500">Keine Mietverhältnisse für diese Auswahl.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

export function LateTable({ late }) {
  if (!late.length) return <div className="rounded-md bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm text-emerald-900" data-testid="os-late-none">Alle Mieter haben im Zeitraum pünktlich bis zur Fälligkeit gezahlt – niemand musste gemahnt werden.</div>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="os-late-table">
        <thead className="bg-stone-50 text-[10px] uppercase tracking-wider text-slate-500">
          <tr>{["Monat", "Mieter", "Objekt / Einheit", "Fällig am", "Soll", "Bis Fälligkeit gezahlt", "Ausgeglichen am", "Gemahnt"].map((h, i) => <th key={h} className={`px-3 py-2.5 font-semibold ${i === 4 || i === 5 ? "text-right" : i >= 3 ? "text-center" : "text-left"}`}>{h}</th>)}</tr>
        </thead>
        <tbody className="divide-y divide-stone-100">
          {late.map((x) => (
            <tr key={`${x.tenancy_id}-${x.month}`} data-testid={`os-late-${x.tenancy_id}-${x.month}`}>
              <td className="px-3 py-2">{monthLabel(x.month)}</td>
              <td className="px-3 py-2 font-medium">{x.tenant_name}</td>
              <td className="px-3 py-2 text-slate-600">{[x.property_label, x.unit_label].filter(Boolean).join(" · ")}</td>
              <td className="px-3 py-2 text-center">{formatDate(x.due)}</td>
              <td className="px-3 py-2 text-right tabular-nums">{formatEUR(x.soll)}</td>
              <td className="px-3 py-2 text-right tabular-nums">{formatEUR(x.paid_by_due)}</td>
              <td className={`px-3 py-2 text-center ${x.paid_on ? "text-amber-700" : "text-rose-700 font-medium"}`}>{x.paid_on ? formatDate(x.paid_on) : "noch offen"}</td>
              <td className="px-3 py-2 text-center">{x.dunned ? <span className="text-rose-700 font-semibold">ja</span> : <span className="text-slate-400">nein</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
